// trigger redeploy
import { responsiveFontSizes } from "@ignite-analytics/theme";
import { designTokens } from "@ignite-analytics/theme/riddle/light";
import { createTheme, ThemeProvider } from "@mui/material";
import { enUS } from "@mui/material/locale";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import { IntlProvider } from "react-intl";
import { BrowserRouter } from "react-router-dom";

import type {} from "@ignite-analytics/theme/themeAugmentation";
import FatalErrorFallback from "./components/FatalErrorBoundary";
import { ApplicationPage } from "./containers/ApplicationPage";
import { AlertProvider } from "./lib/Alerts";
import { FormsApiError } from "./lib/formsApi";
import { loadMessages } from "./lib/i18n/loadMessages";
import { SetLocaleContext } from "./lib/i18n/localeContext";
import { redactInvitationToken } from "./lib/sentry";
import { identify } from "./lib/track";
import { initializeYup } from "./lib/yup";

if (process.env.REACT_APP_HEAP_APP_ID) {
    window.heap.load(process.env.REACT_APP_HEAP_APP_ID);
}

const queryClient = new QueryClient();

const theme = createTheme(designTokens, enUS);
const responsiveTheme = responsiveFontSizes(theme);

function getAppDsn() {
    return process.env.REACT_APP_ASSESSMENT_FORM_SENTRY_DSN
        ? [{ dsn: process.env.REACT_APP_ASSESSMENT_FORM_SENTRY_DSN, release: "" }]
        : [];
}

function getBrowserLocale(): string {
    const lang = navigator.language;
    if (lang) {
        const code = lang.split("-")[0];
        if (code === "no" || code === "nb") {
            return "nb-NO";
        }
        if (code === "sv") {
            return "sv-SE";
        }
        return "en-US";
    }
    return "en-US";
}

function App() {
    const [locale, setLocale] = React.useState<string>(getBrowserLocale());
    const initialized = React.useRef(false);

    initializeYup(locale);

    if (!initialized.current) {
        initialized.current = true;

        Sentry.init({
            dsn: process.env.REACT_APP_SENTRY_DSN,
            transport: Sentry.makeMultiplexedTransport(Sentry.makeFetchTransport, getAppDsn),
            ignoreErrors: ["Object Not Found Matching Id"],
            beforeSend(event, hint) {
                if (hint.originalException instanceof FormsApiError) return null;
                event.tags = event.tags || {};
                event.tags.tenant = event.tags.tenant || localStorage.tenant || "";
                return redactInvitationToken(event);
            },
            beforeSendTransaction: redactInvitationToken,
            integrations: [new Sentry.BrowserTracing()],
            tracesSampleRate: 0.6,
            enabled: process.env.NODE_ENV != "development",
        });

        // adding tracking id
        identify("forms-app-respondent");
    }

    return (
        <React.StrictMode>
            <QueryClientProvider client={queryClient}>
                <ThemeProvider theme={responsiveTheme}>
                    <IntlProvider
                        locale={locale}
                        messages={loadMessages(locale)}
                        onError={(e) => {
                            if (e.code !== "MISSING_TRANSLATION") {
                                console.error(e);
                            }
                        }}
                    >
                        <SetLocaleContext.Provider value={setLocale}>
                            <Sentry.ErrorBoundary fallback={<FatalErrorFallback />}>
                                <BrowserRouter>
                                    <AlertProvider>
                                        <ApplicationPage />
                                    </AlertProvider>
                                </BrowserRouter>
                            </Sentry.ErrorBoundary>
                        </SetLocaleContext.Provider>
                    </IntlProvider>
                </ThemeProvider>
            </QueryClientProvider>
        </React.StrictMode>
    );
}

export default App;
